// src/components/AddProductDialog/AddProductDialog.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './AddProductDialog.css';
import Typography from '../components/Text/Typography';
import SearchCard from '../components/SearchCard/SearchCard';
import LoadingSpinner from '../components/LoadingSpinner';
import ModalDialog from '../components/Dialog/ModalDialog';
import Icon from '../components/Icon/Icon';
import { API_BASE_URL } from '../config/api';

interface Product {
  product_id: number;
  product_name: string;
  brand: string;
  category: string;
  type: string;
  image_url?: string;
}

interface AddProductDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onAddProduct: (productId: number) => void;
}

const AddProductDialog: React.FC<AddProductDialogProps> = ({ isOpen, onClose, onAddProduct }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      fetch(`${API_BASE_URL}/api/products`)
        .then((res) => res.json())
        .then((data) => {
          setProducts(data);
          setLoading(false);
        })
        .catch((err) => {
          console.error('Error fetching products:', err);
          setLoading(false);
        });
    }
  }, [isOpen]);

  const filteredProducts = products.filter((product) =>
    product.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.brand.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleProductClick = (product: Product) => {
    setSelectedProduct(product);
  };

  const handleAddProduct = () => {
    if (selectedProduct) {
      onAddProduct(selectedProduct.product_id);
      setSelectedProduct(null);
      setSearchTerm('');
      onClose();
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  return (
    <ModalDialog
      isOpen={isOpen}
      onClose={onClose}
      title="Add Product to Cabinet"
      primaryButton={{
        label: "Add Product",
        onClick: handleAddProduct,
        disabled: !selectedProduct
      }}
      secondaryButton={{
        label: "Cancel",
        onClick: onClose
      }}
      maxWidth="800px"
      maxHeight="600px"
    >
      <div className="add-product-dialog-content">
        <div className="search-section">
          <div className="search-input-container">
            <div className="search-input-wrapper">
              {!searchTerm && (
                <div className="search-icon">
                  <Icon name="search" size="small" />
                </div>
              )}
              <input
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`search-input-field ${searchTerm ? 'active' : ''}`}
              />
              {searchTerm && (
                <button className="clear-search-button" onClick={handleClearSearch}>
                  <Icon name="close" size="small" />
                </button>
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <LoadingSpinner text="Loading products..." size="small" />
        ) : (
          <div className="products-grid">
            {filteredProducts.map((product) => (
              <SearchCard
                key={product.product_id}
                product={product}
                isSelected={selectedProduct?.product_id === product.product_id}
                onClick={() => handleProductClick(product)}
              />
            ))}
          </div>
        )}
      </div>
    </ModalDialog>
  );
};

export default AddProductDialog;
